/**
 * Team Section - Random Member Rotation
 * BEM Methodology
 */

class TeamSection {
  constructor(section) {
    this.section = section;
    this.membersContainer = section.querySelector('.team-section__members');
    this.memberElements = section.querySelectorAll('.team-section__member');

    this.rotatingIndices = [];
    this.memberElements.forEach((element, index) => {
      if (!element.classList.contains('team-section__member--extra')) {
        this.rotatingIndices.push(index);
      }
    });

    // Get all members data from JSON script tag
    const dataScript = this.membersContainer.querySelector('.team-members-data');
    this.allMembers = dataScript ? JSON.parse(dataScript.textContent) : [];
    
    // Min and Max rotation intervals in milliseconds (5-10 seconds)
    this.minInterval = 8000;
    this.maxInterval = 20000;
    
    // Track which members are currently showing
    this.currentMemberIndices = [];
    this.usedIndices = new Set();
    this.rotationTimers = [];
    
    this.init();
  }

  init() {
    // Check if we're in mobile view (below 1000px)
    this.isMobile = window.innerWidth <= 1000;
    
    // Only start rotation if we have more than 10 members AND we're NOT in mobile view
    if (this.allMembers.length > 10 && !this.isMobile) {
      // Initialize current indices
      this.rotatingIndices.forEach(index => {
        this.currentMemberIndices[index] = index;
        this.usedIndices.add(index);
      });

      // Start rotation
      this.startRotation();
    }
    
    // Listen for resize to stop/start rotation with debounce
    this.resizeTimeout = null;
    this.onResize = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 150);
    };
    window.addEventListener('resize', this.onResize);
  }

  /**
   * Handle window resize
   */
  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 1000;
    
    // If we switched from desktop to mobile, stop rotation
    if (!wasMobile && this.isMobile) {
      this.stopRotation();
    }
    // If we switched from mobile to desktop and have more than 10 members, start rotation
    else if (wasMobile && !this.isMobile && this.allMembers.length > 10) {
      // Initialize current indices if not already done
      if (this.currentMemberIndices.length === 0) {
        this.rotatingIndices.forEach(index => {
          this.currentMemberIndices[index] = index;
          this.usedIndices.add(index);
        });
      }
      this.startRotation();
    }
  }

  /**
   * Get a random member index that hasn't been used recently
   */
  getRandomMemberIndex(excludeIndex) {
    const availableIndices = [];
    
    // If we've used most members, reset the used set but keep current showing
    if (this.usedIndices.size >= this.allMembers.length - 3) {
      this.usedIndices.clear();
      this.currentMemberIndices.forEach(idx => this.usedIndices.add(idx));
    }
    
    // Get indices that haven't been used recently
    for (let i = 0; i < this.allMembers.length; i++) {
      if (!this.usedIndices.has(i) && i !== excludeIndex) {
        availableIndices.push(i);
      }
    }
    
    // If no available, use any except current
    if (availableIndices.length === 0) {
      for (let i = 0; i < this.allMembers.length; i++) {
        if (i !== excludeIndex) {
          availableIndices.push(i);
        }
      }
    }
    
    const randomIndex = Math.floor(Math.random() * availableIndices.length);
    const selectedIndex = availableIndices[randomIndex];
    
    this.usedIndices.add(selectedIndex);
    return selectedIndex;
  }

  /**
   * Update a member card with new data
   */
  updateMemberCard(memberElement, memberData) {
    const imageWrapper = memberElement.querySelector('.team-section__member-image-wrapper');
    const nameElement = memberElement.querySelector('.team-section__member-name');
    const roleElement = memberElement.querySelector('.team-section__member-role');

    // Add fade out
    memberElement.classList.add('team-section__member--fading');
    
    setTimeout(() => {
      // Update image
      imageWrapper.replaceChildren();

      if (memberData.image && memberData.image.length > 0) {
        const image = document.createElement('img');
        image.src = memberData.image;
        image.alt = memberData.name || 'Team Member';
        image.className = 'team-section__member-image';
        image.loading = 'lazy';

        if (memberData.image_srcset) {
          image.srcset = memberData.image_srcset;
          image.sizes = `${imageWrapper.offsetWidth}px`;
        }

        imageWrapper.appendChild(image);
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'team-section__member-image';
        placeholder.style.backgroundColor = '#cfcfcf';
        imageWrapper.appendChild(placeholder);
      }

      // Update name
      if (nameElement && memberData.name) {
        nameElement.textContent = memberData.name;
      }

      if (roleElement) {
        roleElement.textContent = memberData.role || '';
        roleElement.hidden = !memberData.role;
      }

      // Fade in
      memberElement.classList.remove('team-section__member--fading');
    }, 300); // Match CSS transition time
  }

  /**
   * Get random interval between min and max
   */
  getRandomInterval() {
    return Math.floor(Math.random() * (this.maxInterval - this.minInterval + 1)) + this.minInterval;
  }

  /**
   * Rotate a specific member position
   */
  rotateMemberAtPosition(positionIndex) {
    const memberElement = this.memberElements[positionIndex];
    
    if (!memberElement) return;
    
    // Get current member index at this position
    const currentIndex = this.currentMemberIndices[positionIndex];
    
    // Get a new random member
    const newMemberIndex = this.getRandomMemberIndex(currentIndex);
    const newMemberData = this.allMembers[newMemberIndex];
    
    if (newMemberData) {
      // Update the card
      this.updateMemberCard(memberElement, newMemberData);
      
      // Update tracking
      this.currentMemberIndices[positionIndex] = newMemberIndex;
    }
    
    // Schedule next rotation for this position with random interval
    const nextInterval = this.getRandomInterval();
    this.rotationTimers[positionIndex] = setTimeout(() => {
      this.rotateMemberAtPosition(positionIndex);
    }, nextInterval);
  }

  /**
   * Start the rotation for all positions
   */
  startRotation() {
    // Start independent rotation for each member position with random delays
    this.rotatingIndices.forEach(index => {
      // Add initial random delay (0-3 seconds) so they don't all start at once
      const initialDelay = Math.floor(Math.random() * 3000);

      this.rotationTimers[index] = setTimeout(() => {
        this.rotateMemberAtPosition(index);
      }, initialDelay);
    });
  }

  /**
   * Stop rotation (cleanup)
   */
  stopRotation() {
    // Clear all timers
    this.rotationTimers.forEach(timer => {
      if (timer) {
        clearTimeout(timer);
      }
    });
    this.rotationTimers = [];
  }

  /**
   * Destroy instance
   */
  destroy() {
    this.stopRotation();
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimeout);
  }
}

function initTeamSection(section) {
  if (section.teamSectionInstance) {
    section.teamSectionInstance.destroy();
  }

  section.teamSectionInstance = new TeamSection(section);
}

// Initialize all team sections when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.team-section').forEach(initTeamSection);
});

// Handle Shopify theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.team-section');
    if (section) {
      initTeamSection(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.team-section');
    if (section && section.teamSectionInstance) {
      section.teamSectionInstance.destroy();
      section.teamSectionInstance = null;
    }
  });
}
